import {
	queryOptions,
	useMutation,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { Result } from "@bidbattles/bloks/lib/result";
import { toast } from "sonner";
import { m } from "@/paraglide/messages";
import { env } from "@/env";
import { BBBackendClient } from "@bidbattles/bloks/lib/rapace";
import { swInterop } from "./sw-interop";
import { createServerFn } from "@tanstack/react-start";

async function supported(): Promise<boolean> {
	if (!("serviceWorker" in navigator && "PushManager" in window)) {
		return false;
	}

	const result = await swInterop.withRegistration(() => Result.ok(null));
	return result.ok;
}

async function requestPermission(): Promise<Result<boolean>> {
	return Result.flatten(
		await Result.tryAsync(async () => {
			if (!(await supported())) {
				return Result.ok(false);
			}

			const permissionResult = await Notification.requestPermission();
			return Result.ok(permissionResult === "granted");
		}),
	);
}

async function getSubscription(): Promise<Result<PushSubscription | null>> {
	return Result.flatten(
		await Result.tryAsync(async () => {
			if (!(await supported())) {
				return Result.ok(null);
			}

			return await swInterop.withRegistration(async (registration) => {
				return Result.ok(await registration.pushManager.getSubscription());
			});
		}),
	);
}

async function ensureSubscription(
	publicKey: string,
): Promise<Result<PushSubscription>> {
	return Result.flatten(
		await Result.tryAsync(async () => {
			const getResult = await getSubscription();
			if (!getResult.ok) {
				return getResult;
			}

			if (getResult.data) {
				return Result.ok(getResult.data);
			}

			return await swInterop.withRegistration(async (registration) => {
				return Result.ok(
					await registration.pushManager.subscribe({
						userVisibleOnly: true,
						applicationServerKey: publicKey,
					}),
				);
			});
		}),
	);
}

async function revokeSubscription(): Promise<Result<boolean>> {
	return Result.flatten(
		await Result.tryAsync(async () => {
			const subResult = await getSubscription();
			if (!subResult.ok) {
				return subResult;
			}

			if (!subResult.data) {
				return Result.ok(true);
			}

			return Result.ok(await subResult.data.unsubscribe());
		}),
	);
}

async function hasPermission(): Promise<Result<boolean>> {
	if (!(await supported())) {
		return Result.ok(false);
	}

	return Result.ok(Notification.permission === "granted");
}

const queries = {
	supported: queryOptions({
		queryKey: ["pn", "supported"],
		queryFn: async () => await supported(),
		staleTime: 0,
	}),
	permission: queryOptions({
		queryKey: ["pn", "permission"],
		queryFn: async () => Result.unwrap(await hasPermission()),
		staleTime: 0,
	}),
	subscription: queryOptions({
		queryKey: ["pn", "subscription"],
		queryFn: async () => Result.unwrapOr(await getSubscription(), null),
		staleTime: 0,
	}),
};

const getPublicKeyServerFn = createServerFn().handler(
	async (): Promise<Result<string, never>> => {
		return Result.ok(env.VAPID_PUBLIC_KEY);
	},
);
const getPublicKey = async () =>
	Result.flatten(await Result.tryAsync(getPublicKeyServerFn));

function usePermission(params: { publicKey?: string } = {}) {
	const { data: _supported } = useSuspenseQuery(queries.supported);

	const { data: pnPermission } = useSuspenseQuery(queries.permission);
	const permissionGranted = pnPermission === true;

	const { data: pnSubscription } = useSuspenseQuery(queries.subscription);

	const requestPermissionMutation = useMutation({
		mutationKey: ["pn"],
		mutationFn: async (publicKey?: string) => {
			const [pubkeyResult, permResult] = await Promise.all([
				getPublicKey(),
				requestPermission(),
			]);

			let _publicKey = publicKey;
			if (!_publicKey) {
				if (!pubkeyResult.ok) {
					toast.error(m.error_toast({ message: pubkeyResult.error.message }));
					return;
				}
				_publicKey = pubkeyResult.data;
			}

			if (!permResult.ok || !permResult.data) {
				toast.error("Permission denied or push notifications not supported");
				return;
			}

			const subResult = await ensureSubscription(_publicKey);
			if (!subResult.ok) {
				toast.error(m.error_toast({ message: subResult.error.message }));
				return;
			}
			const subscription = subResult.data.toJSON();

			const endpoint = subscription.endpoint;
			const authKey = subscription.keys?.auth;
			const p256dhKey = subscription.keys?.p256dh;

			if (!endpoint || !authKey || !p256dhKey) {
				await revokeSubscription();
				toast.error(m.error_toast({ message: "Missing required keys." }));
				return;
			}

			const serverSubResult =
				await BBBackendClient.ensureNotificationSubscription({
					endpoint: endpoint,
					auth: authKey,
					p256dh: p256dhKey,
				});

			if (!serverSubResult.ok) {
				await revokeSubscription();
				toast.error(m.error_toast({ message: serverSubResult.error.message }));
				return;
			}

			toast.info("Push notifications enabled");
		},
	});

	const revokeSubscriptionMutation = useMutation({
		mutationKey: ["pn"],
		mutationFn: async () => {
			const result = await revokeSubscription();
			if (!result.ok) {
				toast.error(m.error_toast({ message: result.error.message }));
				return;
			}

			toast.success("Push notifications disabled");
		},
	});

	return {
		supported: _supported,
		permissionGranted,
		subscription: pnSubscription,
		enabling: requestPermissionMutation.isPending,
		disabling: revokeSubscriptionMutation.isPending,
		toggling:
			requestPermissionMutation.isPending ||
			revokeSubscriptionMutation.isPending,
		enable: async () =>
			await requestPermissionMutation.mutateAsync(params.publicKey),
		disable: revokeSubscriptionMutation.mutateAsync,
	};
}

export const webPush = {
	queryOptions: queries,
	supported,
	requestPermission,
	hasPermission,
	usePermission,
};
